import { useEffect, useMemo, useState } from "react";
import "./styles.css";
import TacSidebar from "./TacSidebar.jsx";
import Dashboard from "./Dashboard.jsx";
import OperationsBoard from "./OperationsBoard.jsx";
import OperationModal from "./OperationModal.jsx";
import OperationsPlanning from "./OperationsPlanning.jsx";
import BriefingPackets from "./BriefingPackets.jsx";
import TacticalMap from "./TacticalMap.jsx";
import LiveOperations from "./LiveOperations.jsx";
import RosterManager from "./RosterManager.jsx";
import AssignmentBoard from "./AssignmentBoard.jsx";
import "leaflet/dist/leaflet.css";

const LOCAL_OPERATIONS_KEY = "tacops_operations_v8_fallback";
const EQUIPMENT_KEY = "tacops_equipment_v1";
const ROSTER_KEY = "tacops_roster_v1";
const OPERATIONS_PLANS_KEY = "tacops_operations_plans_v8";
const MAP_ZONES_KEY = "tacops_map_zones_v1";
const ASSIGNMENTS_KEY = "tacops_assignments_v1";

const starterOperations = [
  {
    id: "operation_demo_1",
    title: "Operation Planning",
    operationType: "Search Warrant",
    missionType: "Search Warrant",
    priority: "High",
    status: "Planning",
    location: "Saguache County",
    objective: "Plan tactical deployment with coordinated staging, entry, perimeter, medical, and communications contingencies.",
    commander: "Team Commander",
    teamLead: "Entry Team Lead",
    staging: "Primary staging TBD",
    operationDate: "",
    assignedOperators: "Entry: TBD\nPerimeter: TBD\nLess-lethal: TBD\nMedical: TBD",
    equipment: "Armor, helmets, radios, ram, shield, medical bag",
    safety: "Confirm known weapons, occupants, animals, escape routes, EMS staging, and communications plan.",
    notes: "",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    timeline: [{ id: "tl_1", time: new Date().toISOString(), title: "Operation created", detail: "Initial TacOps operation shell created." }],
    aar: null
  }
];

const starterEquipment = [
  { id: "eq_1", name: "Body Armor / Plates", category: "Armor", ready: 31, total: 34, status: "Ready", notes: "Operator issued." },
  { id: "eq_2", name: "Ballistic Helmets", category: "Armor", ready: 15, total: 15, status: "Ready", notes: "Inspection current." },
  { id: "eq_3", name: "Radio Systems", category: "Comms", ready: 12, total: 15, status: "Check", notes: "Battery cycle due." },
  { id: "eq_4", name: "Thermal Devices", category: "Optics", ready: 6, total: 6, status: "Ready", notes: "Assigned to operators." },
  { id: "eq_5", name: "Shield / Mini Shield", category: "Protection", ready: 2, total: 2, status: "Ready", notes: "Verify staging." },
  { id: "eq_6", name: "Breaching Ram", category: "Breaching", ready: 1, total: 1, status: "Ready", notes: "Staged." }
];

const starterRoster = [
  { id: "op_1", name: "Commander", role: "Commander", status: "Available", certs: "Command / Planning" },
  { id: "op_2", name: "Team Leader", role: "Team Leader", status: "Available", certs: "Entry / Team Lead" },
  { id: "op_3", name: "Entry Operator", role: "Entry", status: "Available", certs: "Basic Tactical" },
  { id: "op_4", name: "Less-Lethal Operator", role: "Less-Lethal", status: "Training", certs: "Less-Lethal" },
  { id: "op_5", name: "Breacher", role: "Breacher", status: "Available", certs: "Mechanical Breaching" },
  { id: "op_6", name: "Drone / Overwatch", role: "Drone", status: "Available", certs: "UAS / Thermal" }
];

async function api(path, options = {}) {
  const res = await fetch(path, {
    credentials: "include",
    headers: { "content-type": "application/json", ...(options.headers || {}) },
    ...options
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
}

function loadList(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) && parsed.length ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function saveList(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

function makeId(prefix = "id") {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function normalizeOperation(row) {
  return {
    id: row.id,
    title: row.title || "",
    operationType: row.operation_type || row.mission_type || row.operationType || row.missionType || "Other",
    missionType: row.operation_type || row.mission_type || row.operationType || row.missionType || "Other",
    priority: row.priority || "High",
    status: row.status || "Planning",
    location: row.location || "",
    objective: row.objective || "",
    commander: row.commander || "",
    teamLead: row.team_lead || row.teamLead || "",
    staging: row.staging || "",
    operationDate: row.operation_date || row.operationDate || "",
    assignedOperators: row.assigned_operators || row.assignedOperators || "",
    equipment: row.equipment || "",
    safety: row.safety || "",
    notes: row.notes || "",
    createdAt: row.created_at || row.createdAt || "",
    updatedAt: row.updated_at || row.updatedAt || "",
    timeline: Array.isArray(row.timeline) ? row.timeline : safeJson(row.timeline, []),
    aar: row.aar && typeof row.aar === "object" ? row.aar : safeJson(row.aar, null)
  };
}

function toServerOperation(op) {
  return {
    id: op.id,
    title: op.title,
    mission_type: op.operationType || op.missionType,
    priority: op.priority,
    status: op.status,
    location: op.location,
    objective: op.objective,
    commander: op.commander,
    team_lead: op.teamLead,
    staging: op.staging,
    operation_date: op.operationDate,
    assigned_operators: op.assignedOperators,
    equipment: op.equipment,
    safety: op.safety,
    notes: op.notes,
    timeline: op.timeline || [],
    aar: op.aar || null
  };
}

function safeJson(value, fallback) {
  try {
    if (!value) return fallback;
    if (typeof value !== "string") return value;
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

export default function App() {
  const [nav, setNav] = useState("dashboard");
  const [operations, setOperations] = useState(() => loadList(LOCAL_OPERATIONS_KEY, starterOperations));
  const [equipment, setEquipment] = useState(() => loadList(EQUIPMENT_KEY, starterEquipment));
  const [roster, setRoster] = useState(() => loadList(ROSTER_KEY, starterRoster));
  const [operationsPlans, setOperationsPlansState] = useState(() => loadList(OPERATIONS_PLANS_KEY, []));
  const [mapZones, setMapZonesState] = useState(() => loadList(MAP_ZONES_KEY, []));
  const [assignments, setAssignmentsState] = useState(() => loadList(ASSIGNMENTS_KEY, []));
  const [modalOpen, setModalOpen] = useState(false);
  const [editingOperation, setEditingOperation] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [syncStatus, setSyncStatus] = useState("Local fallback");

  useEffect(() => {
    loadOperationsFromD1();
  }, []);

  useEffect(() => saveList(EQUIPMENT_KEY, equipment), [equipment]);
  useEffect(() => saveList(ROSTER_KEY, roster), [roster]);
  useEffect(() => saveList(ASSIGNMENTS_KEY, assignments), [assignments]);

  function setOperationsPlans(next) {
    setOperationsPlansState(next);
    saveList(OPERATIONS_PLANS_KEY, next);
  }

  function setMapZones(next) {
    setMapZonesState(next);
    saveList(MAP_ZONES_KEY, next);
  }

  function setAssignments(next) {
    setAssignmentsState(next);
    saveList(ASSIGNMENTS_KEY, next);
  }

  async function loadOperationsFromD1() {
    try {
      const data = await api("/api/missions");
      const serverOperations = (data.missions || []).map(normalizeOperation);
      if (serverOperations.length) {
        setOperations(serverOperations);
        saveList(LOCAL_OPERATIONS_KEY, serverOperations);
      }
      setSyncStatus("D1 connected");
    } catch {
      setSyncStatus("Local fallback");
    }
  }

  const stats = useMemo(() => {
    const active = operations.filter((op) => ["Approved", "Active", "Planning"].includes(op.status)).length;
    const critical = operations.filter((op) => op.priority === "Critical").length;
    const aarDue = operations.filter((op) => op.status === "Debrief" && !op.aar).length;
    const closed = operations.filter((op) => op.status === "Closed").length;
    const equipmentReady = equipment.reduce((sum, item) => sum + Number(item.ready || 0), 0);
    const equipmentTotal = equipment.reduce((sum, item) => sum + Number(item.total || 0), 0);
    const equipmentPct = equipmentTotal ? Math.round((equipmentReady / equipmentTotal) * 100) : 100;
    const availableOperators = roster.filter((x) => x.status === "Available").length;
    return { total: operations.length, active, critical, aarDue, closed, equipmentPct, availableOperators, rosterTotal: roster.length };
  }, [operations, equipment, roster]);

  function setAndCache(next) {
    setOperations(next);
    saveList(LOCAL_OPERATIONS_KEY, next);
  }

  function openCreateOperation() {
    setEditingOperation(null);
    setModalOpen(true);
  }

  function openEditOperation(operation) {
    setEditingOperation(operation);
    setModalOpen(true);
  }

  async function saveOperation(form) {
    const now = new Date().toISOString();
    const normalizedForm = { ...form, operationType: form.operationType || form.missionType, missionType: form.operationType || form.missionType };
    let saved;

    if (normalizedForm.id) {
      const oldOperation = operations.find((op) => op.id === normalizedForm.id) || {};
      saved = {
        ...oldOperation,
        ...normalizedForm,
        updatedAt: now,
        timeline: [
          ...(Array.isArray(oldOperation.timeline) ? oldOperation.timeline : []),
          { id: makeId("tl"), time: now, title: "Operation updated", detail: `Status: ${normalizedForm.status || "Updated"}` }
        ]
      };
    } else {
      saved = {
        ...normalizedForm,
        id: makeId("operation"),
        createdAt: now,
        updatedAt: now,
        timeline: [{ id: makeId("tl"), time: now, title: "Operation created", detail: "Operation added to TacOps Operations Board." }],
        aar: null
      };
    }

    const optimistic = normalizedForm.id ? operations.map((op) => (op.id === normalizedForm.id ? saved : op)) : [saved, ...operations];
    setAndCache(optimistic);

    try {
      const data = await api("/api/missions", {
        method: "POST",
        body: JSON.stringify(toServerOperation(saved))
      });
      const serverOperation = normalizeOperation(data.mission || saved);
      setAndCache(optimistic.map((op) => (op.id === serverOperation.id ? serverOperation : op)));
      setSyncStatus("D1 connected");
    } catch {
      setSyncStatus("Local fallback");
    }

    setModalOpen(false);
    setEditingOperation(null);
  }

  async function deleteOperation(id) {
    if (!confirm("Delete this operation?")) return;
    const next = operations.filter((op) => op.id !== id);
    setAndCache(next);
    try {
      await api(`/api/missions?id=${encodeURIComponent(id)}`, { method: "DELETE" });
      setSyncStatus("D1 connected");
    } catch {
      setSyncStatus("Local fallback");
    }
  }

  async function quickStatus(id, status) {
    const now = new Date().toISOString();
    const target = operations.find((op) => op.id === id);
    if (!target) return;

    const updated = {
      ...target,
      status,
      updatedAt: now,
      timeline: [
        ...(Array.isArray(target.timeline) ? target.timeline : []),
        { id: makeId("tl"), time: now, title: `Status changed to ${status}`, detail: "Quick status update from Operations Board." }
      ]
    };

    const next = operations.map((op) => (op.id === id ? updated : op));
    setAndCache(next);

    try {
      await api("/api/missions", { method: "POST", body: JSON.stringify(toServerOperation(updated)) });
      setSyncStatus("D1 connected");
    } catch {
      setSyncStatus("Local fallback");
    }
  }

  async function createAar(operation) {
    const now = new Date().toISOString();
    const aar = {
      createdAt: now,
      operationName: operation.title,
      operationDate: operation.operationDate,
      location: operation.location,
      commander: operation.commander,
      missionType: operation.operationType,
      objective: operation.objective,
      synopsis: "",
      whatWentWell: "",
      deficiencies: "",
      safetyConcerns: "",
      equipmentIssues: "",
      communicationIssues: "",
      trainingRecommendations: "",
      correctiveActions: "",
      followUpAssignments: ""
    };

    const updated = {
      ...operation,
      status: "Debrief",
      aar,
      updatedAt: now,
      timeline: [...(Array.isArray(operation.timeline) ? operation.timeline : []), { id: makeId("tl"), time: now, title: "AAR started", detail: "After-action review shell generated." }]
    };

    setAndCache(operations.map((op) => (op.id === operation.id ? updated : op)));

    try {
      await api("/api/missions", { method: "POST", body: JSON.stringify(toServerOperation(updated)) });
      setSyncStatus("D1 connected");
    } catch {
      setSyncStatus("Local fallback");
    }

    setNav("aars");
  }

  function exportAarPdf(operation) {
    const aar = operation.aar || {};
    const win = window.open("", "_blank");
    if (!win) return alert("Popup blocked. Allow popups and try again.");
    const esc = (value) => String(value || "").replace(/[&<>"']/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" }[m]));
    win.document.write(`
      <html><head><title>TacOps AAR - ${esc(operation.title)}</title>
      <style>body{font-family:Arial,sans-serif;margin:36px;color:#111}h1{margin-bottom:4px;letter-spacing:.04em}h2{border-bottom:1px solid #999;padding-bottom:4px;margin-top:24px}.sub{color:#555;margin-bottom:24px}.grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}.box{border:1px solid #ccc;padding:10px;margin:8px 0}.label{font-size:11px;color:#555;text-transform:uppercase;font-weight:bold}.value{margin-top:4px;white-space:pre-wrap}@media print{button{display:none}}</style>
      </head><body>
      <button onclick="window.print()">Print / Save as PDF</button>
      <h1>TacOps After-Action Review</h1><div class="sub">TacOps by Aegis Group</div>
      <div class="grid">
        <div class="box"><div class="label">Operation</div><div class="value">${esc(aar.operationName || operation.title)}</div></div>
        <div class="box"><div class="label">Date</div><div class="value">${esc(aar.operationDate || operation.operationDate)}</div></div>
        <div class="box"><div class="label">Location</div><div class="value">${esc(aar.location || operation.location)}</div></div>
        <div class="box"><div class="label">Commander</div><div class="value">${esc(aar.commander || operation.commander)}</div></div>
        <div class="box"><div class="label">Operation Type</div><div class="value">${esc(aar.missionType || operation.operationType)}</div></div>
        <div class="box"><div class="label">Objective</div><div class="value">${esc(aar.objective || operation.objective)}</div></div>
      </div>
      <h2>Timeline</h2>${(operation.timeline || []).map(t => `<div class="box"><div class="label">${esc(new Date(t.time).toLocaleString())}</div><div class="value"><b>${esc(t.title)}</b><br>${esc(t.detail)}</div></div>`).join("")}
      <h2>Outcome</h2><div class="box"><div class="value">${esc(aar.outcome)}</div></div>
      <h2>Personnel / Assignments</h2><div class="box"><div class="value">${esc(aar.personnelSummary || operation.assignedOperators)}</div></div>
      <h2>Synopsis</h2><div class="box"><div class="value">${esc(aar.synopsis)}</div></div>
      <h2>What Went Well</h2><div class="box"><div class="value">${esc(aar.whatWentWell)}</div></div>
      <h2>Deficiencies / Issues</h2><div class="box"><div class="value">${esc(aar.deficiencies)}</div></div>
      <h2>Safety Concerns</h2><div class="box"><div class="value">${esc(aar.safetyConcerns)}</div></div>
      <h2>Equipment / Communications</h2><div class="box"><div class="value">${esc(aar.equipmentIssues)}<br>${esc(aar.communicationIssues)}</div></div>
      <h2>Training Recommendations</h2><div class="box"><div class="value">${esc(aar.trainingRecommendations)}</div></div>
      <h2>Corrective Actions / Follow-Up</h2><div class="box"><div class="value">${esc(aar.correctiveActions)}<br>${esc(aar.followUpAssignments)}</div></div>
      </body></html>
    `);
    win.document.close();
  }


  function updateOperationFromLive(updatedOperation) {
    const next = operations.map((op) =>
      op.id === updatedOperation.id ? updatedOperation : op
    );

    setAndCache(next);

    api("/api/missions", {
      method: "POST",
      body: JSON.stringify(toServerOperation(updatedOperation))
    })
      .then(() => setSyncStatus("D1 connected"))
      .catch(() => setSyncStatus("Local fallback"));
  }

  const currentView =
    nav === "dashboard" ? (
      <Dashboard missions={operations} stats={stats} equipment={equipment} roster={roster} onCreateMission={openCreateOperation} onOpenMission={openEditOperation} setNav={setNav} />
    ) : nav === "operations" ? (
      <OperationsBoard operations={operations} onCreate={openCreateOperation} onEdit={openEditOperation} onDelete={deleteOperation} onStatus={quickStatus} onCreateAar={createAar} />
    ) : nav === "callouts" ? (
      <OperationsBoard title="Tactical Callouts" filterTypes={["Search Warrant", "Arrest Warrant", "Barricaded Subject", "GPS Placement", "Surveillance"]} operations={operations} onCreate={openCreateOperation} onEdit={openEditOperation} onDelete={deleteOperation} onStatus={quickStatus} onCreateAar={createAar} />
    ) : nav === "planning" ? (
      <OperationsPlanning operations={operations} operationsPlans={operationsPlans} setOperationsPlans={setOperationsPlans} onOpenOperation={openEditOperation} />
    ) : nav === "packets" ? (
      <BriefingPackets missions={operations} warrantPlans={operationsPlans} />
    ) : nav === "map" ? (
      <TacticalMap missions={operations} warrantPlans={operationsPlans} mapZones={mapZones} setMapZones={setMapZones} />
    ) : nav === "equipment" ? (
      <EquipmentReadiness equipment={equipment} setEquipment={setEquipment} />
    ) : nav === "roster" ? (
      <RosterManager roster={roster} setRoster={setRoster} />
    ) : nav === "assignments" ? (
      <AssignmentBoard
        operations={operations}
        roster={roster}
        setRoster={setRoster}
        assignments={assignments}
        setAssignments={setAssignments}
      />
    ) : nav === "checklists" ? (
      <OperationalChecklist operations={operations} />
    ) : nav === "threats" ? (
      <ThreatMatrix operations={operations} />
    ) : nav === "whiteboard" ? (
      <CommandWhiteboard operations={operations} />
    ) : nav === "training" ? (
      <TrainingTracker roster={roster} />
    ) : nav === "aars" ? (
      <Aars operations={operations} onExport={exportAarPdf} onEditOperation={openEditOperation} onSave={updateOperationFromLive} />
    ) : nav === "live" ? (
      <LiveOperations
        operations={operations}
        onUpdateOperation={updateOperationFromLive}
      />
    ) : nav === "timeline" ? (
      <Timeline operations={operations} onUpdateOperation={updateOperationFromLive} />
    ) : (
      <Placeholder title="Module Coming Next" subtitle="This TacOps module shell is ready to connect to operation data." />
    );

  return (
    <div className={`app-shell ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
      <TacSidebar nav={nav} setNav={setNav} stats={stats} collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed} />
      <main className="main-panel">
        <header className="topbar">
          <div>
            <div className="eyebrow">TacOps by Aegis Group</div>
            {nav !== "dashboard" && (
              <h1>{navTitle(nav)}</h1>
            )}
          </div>
          <div className="status-ribbon">
            <span>{syncStatus}</span>
            <span>{new Date().toLocaleDateString()}</span>
            <span className="status-good">SYSTEM OPERATIONAL</span>
          </div>
        </header>
        {currentView}
      </main>

      {modalOpen && (
        <OperationModal
          operation={editingOperation}
          onClose={() => {
            setModalOpen(false);
            setEditingOperation(null);
          }}
          onSave={saveOperation}
        />
      )}
    </div>
  );
}

function navTitle(nav) {
  const titles = {
    operations: "Operations Board",
    callouts: "Tactical Callouts",
    planning: "Operations Planning",
    timeline: "Operations Timeline",
    live: "Live Operations",
    packets: "Operations Packet",
    map: "Tactical Map / Staging",
    equipment: "Equipment Readiness",
    roster: "Team Roster",
    assignments: "Personnel Assignments",
    checklists: "Operational Checklists",
    threats: "Threat Matrix",
    whiteboard: "Command Whiteboard",
    training: "Training Tracker",
    aars: "After-Action Reviews",
    admin: "Admin"
  };
  return titles[nav] || "TacOps";
}

function Placeholder({ title, subtitle }) {
  return <section className="panel"><h2>{title}</h2><p className="muted">{subtitle}</p></section>;
}

const CHECKLIST_KEY = "tacops_operation_checklists_v1";
const THREAT_KEY = "tacops_threat_matrix_v1";
const WHITEBOARD_KEY = "tacops_command_whiteboard_v1";
const TRAINING_KEY = "tacops_training_tracker_v1";

function loadObject(key, fallback = {}) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function saveObject(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

const checklistTemplates = {
  "Search Warrant": [
    "Warrant signed and verified",
    "Briefing complete",
    "Radio check complete",
    "Command assigned",
    "Entry team assigned",
    "Perimeter assigned",
    "Less lethal assigned",
    "Medical / CCP assigned",
    "Route in confirmed",
    "Route out confirmed",
    "Evidence collection plan confirmed",
    "Transport plan confirmed",
    "AAR notes started"
  ],
  "Arrest Warrant": [
    "Warrant confirmed active",
    "Briefing complete",
    "Subject identifiers confirmed",
    "Perimeter assigned",
    "Arrest team assigned",
    "Less lethal assigned",
    "Medical plan confirmed",
    "Transport plan confirmed",
    "Booking plan confirmed"
  ],
  "Barricaded Subject": [
    "Command post established",
    "Containment assigned",
    "Negotiations assigned",
    "Medical staged",
    "Evacuation considerations reviewed",
    "Radio plan confirmed",
    "Relief plan considered",
    "AAR notes started"
  ],
  default: [
    "Operation created",
    "Briefing complete",
    "Assignments complete",
    "Equipment checked",
    "Radio plan confirmed",
    "Medical plan confirmed",
    "Route in/out confirmed",
    "AAR notes started"
  ]
};

function OperationalChecklist({ operations }) {
  const [selectedOperationId, setSelectedOperationId] = useState(operations[0]?.id || "");
  const [saved, setSaved] = useState(() => loadObject(CHECKLIST_KEY, {}));
  const selectedOperation = operations.find((op) => op.id === selectedOperationId) || operations[0];
  const opId = selectedOperation?.id || "default";
  const template = checklistTemplates[selectedOperation?.operationType] || checklistTemplates[selectedOperation?.missionType] || checklistTemplates.default;
  const items = saved[opId]?.items || template.map((text, index) => ({ id: `chk_${index}`, text, done: false }));
  const completed = items.filter((item) => item.done).length;
  const pct = items.length ? Math.round((completed / items.length) * 100) : 0;
  const [customText, setCustomText] = useState("");

  function persist(nextItems) {
    const next = { ...saved, [opId]: { operationId: opId, updatedAt: new Date().toISOString(), items: nextItems } };
    setSaved(next);
    saveObject(CHECKLIST_KEY, next);
  }

  function toggle(id) {
    persist(items.map((item) => item.id === id ? { ...item, done: !item.done } : item));
  }

  function addCustom(e) {
    e.preventDefault();
    if (!customText.trim()) return;
    persist([...items, { id: `chk_${Date.now()}`, text: customText.trim(), done: false }]);
    setCustomText("");
  }

  function resetTemplate() {
    if (!confirm("Reset this operation checklist to the default template?")) return;
    persist(template.map((text, index) => ({ id: `chk_${Date.now()}_${index}`, text, done: false })));
  }

  return (
    <section>
      <div className="section-head">
        <div>
          <h2>Operational Checklist Engine</h2>
          <p className="muted">Operation-specific readiness checks for briefing, deployment, and AAR preparation.</p>
        </div>
        <div className="checklist-percent tone-green"><b>{pct}%</b><span>Complete</span></div>
      </div>

      <div className="panel ops-tool-panel">
        <label>
          <span>Operation</span>
          <select value={selectedOperation?.id || ""} onChange={(e) => setSelectedOperationId(e.target.value)}>
            {operations.map((op) => <option key={op.id} value={op.id}>{op.title}</option>)}
          </select>
        </label>
        <div className="progress-wrap ops-progress"><div className="progress"><span style={{ width: `${pct}%` }} /></div><strong>{completed}/{items.length}</strong></div>
      </div>

      <div className="checklist-grid">
        {items.map((item) => (
          <button key={item.id} className={`checklist-item ${item.done ? "done" : ""}`} onClick={() => toggle(item.id)}>
            <span>{item.done ? "☑" : "☐"}</span>
            <b>{item.text}</b>
          </button>
        ))}
      </div>

      <form className="panel ops-tool-panel" onSubmit={addCustom}>
        <label>
          <span>Add Custom Checklist Item</span>
          <input value={customText} onChange={(e) => setCustomText(e.target.value)} placeholder="Example: Power company notified" />
        </label>
        <div className="ops-tool-actions">
          <button className="primary" type="submit">Add Item</button>
          <button type="button" className="danger-text" onClick={resetTemplate}>Reset Template</button>
        </div>
      </form>
    </section>
  );
}

const threatFactors = [
  "Weapons History",
  "Violent Criminal History",
  "Known Drug Trafficking",
  "Prior Resistance / Flight",
  "Known Associates",
  "Animals On Site",
  "Fortifications / Cameras",
  "Mental Health / Crisis Concerns",
  "Children / Vulnerable Persons",
  "Unknown Occupants"
];

function threatLevel(score) {
  if (score >= 24) return { label: "CRITICAL", tone: "tone-red" };
  if (score >= 16) return { label: "HIGH", tone: "tone-gold" };
  if (score >= 8) return { label: "MODERATE", tone: "tone-blue" };
  return { label: "LOW", tone: "tone-green" };
}

function ThreatMatrix({ operations }) {
  const [selectedOperationId, setSelectedOperationId] = useState(operations[0]?.id || "");
  const [saved, setSaved] = useState(() => loadObject(THREAT_KEY, {}));
  const selectedOperation = operations.find((op) => op.id === selectedOperationId) || operations[0];
  const opId = selectedOperation?.id || "default";
  const current = saved[opId] || { factors: {}, notes: "", targetSummary: "" };
  const score = threatFactors.reduce((sum, factor) => sum + Number(current.factors?.[factor] || 0), 0);
  const level = threatLevel(score);

  function persist(patch) {
    const nextCurrent = { ...current, ...patch, updatedAt: new Date().toISOString() };
    const next = { ...saved, [opId]: nextCurrent };
    setSaved(next);
    saveObject(THREAT_KEY, next);
  }

  function updateFactor(factor, value) {
    persist({ factors: { ...(current.factors || {}), [factor]: Number(value) } });
  }

  return (
    <section>
      <div className="section-head">
        <div>
          <h2>Threat Matrix</h2>
          <p className="muted">Score target and location risk factors to support planning, briefing, and resource decisions.</p>
        </div>
        <div className={`threat-score-card ${level.tone}`}><b>{level.label}</b><span>Score {score}</span></div>
      </div>

      <div className="panel ops-tool-panel">
        <label>
          <span>Operation</span>
          <select value={selectedOperation?.id || ""} onChange={(e) => setSelectedOperationId(e.target.value)}>
            {operations.map((op) => <option key={op.id} value={op.id}>{op.title}</option>)}
          </select>
        </label>
        <label>
          <span>Target / Location Summary</span>
          <textarea value={current.targetSummary || ""} onChange={(e) => persist({ targetSummary: e.target.value })} placeholder="Brief subject/location threat summary..." />
        </label>
      </div>

      <div className="threat-grid">
        {threatFactors.map((factor) => (
          <div className="threat-factor" key={factor}>
            <div><b>{factor}</b><small>0 none / unknown • 3 elevated • 5 critical</small></div>
            <select value={current.factors?.[factor] || 0} onChange={(e) => updateFactor(factor, e.target.value)}>
              <option value="0">0 - None / Unknown</option>
              <option value="1">1 - Low</option>
              <option value="3">3 - Elevated</option>
              <option value="5">5 - Critical</option>
            </select>
          </div>
        ))}
      </div>

      <div className="panel ops-tool-panel">
        <label>
          <span>Threat Notes / Mitigation</span>
          <textarea rows="5" value={current.notes || ""} onChange={(e) => persist({ notes: e.target.value })} placeholder="Weapons, animals, fortifications, known associates, entry concerns, mitigation steps..." />
        </label>
      </div>
    </section>
  );
}

function CommandWhiteboard({ operations }) {
  const [selectedOperationId, setSelectedOperationId] = useState(operations[0]?.id || "");
  const [saved, setSaved] = useState(() => loadObject(WHITEBOARD_KEY, {}));
  const selectedOperation = operations.find((op) => op.id === selectedOperationId) || operations[0];
  const opId = selectedOperation?.id || "default";
  const board = saved[opId] || { priorityTasks: "", intelUpdates: "", outstandingActions: "", safetyNotes: "", commandNotes: "" };

  function persist(field, value) {
    const nextBoard = { ...board, [field]: value, updatedAt: new Date().toISOString() };
    const next = { ...saved, [opId]: nextBoard };
    setSaved(next);
    saveObject(WHITEBOARD_KEY, next);
  }

  return (
    <section>
      <div className="section-head">
        <div>
          <h2>Command Whiteboard</h2>
          <p className="muted">A simple command-post board for priority tasks, intel updates, safety notes, and unresolved actions.</p>
        </div>
      </div>
      <div className="panel ops-tool-panel">
        <label>
          <span>Operation</span>
          <select value={selectedOperation?.id || ""} onChange={(e) => setSelectedOperationId(e.target.value)}>
            {operations.map((op) => <option key={op.id} value={op.id}>{op.title}</option>)}
          </select>
        </label>
        <small>Last updated: {board.updatedAt ? new Date(board.updatedAt).toLocaleString() : "Not yet"}</small>
      </div>
      <div className="whiteboard-grid">
        {[
          ["priorityTasks", "Priority Tasks"],
          ["intelUpdates", "Intel Updates"],
          ["outstandingActions", "Outstanding Actions"],
          ["safetyNotes", "Safety Notes"],
          ["commandNotes", "Command Notes"]
        ].map(([field, label]) => (
          <label className="whiteboard-card" key={field}>
            <span>{label}</span>
            <textarea rows="8" value={board[field] || ""} onChange={(e) => persist(field, e.target.value)} placeholder={`Add ${label.toLowerCase()}...`} />
          </label>
        ))}
      </div>
    </section>
  );
}

const trainingAreas = ["CQB", "Pistol", "Rifle", "Less Lethal", "Medical", "Breaching", "Drone", "Command / Planning"];

function TrainingTracker({ roster }) {
  const [saved, setSaved] = useState(() => loadObject(TRAINING_KEY, {}));
  const [selectedOperatorId, setSelectedOperatorId] = useState(roster[0]?.id || "");
  const selectedOperator = roster.find((op) => op.id === selectedOperatorId) || roster[0];
  const opId = selectedOperator?.id || "default";
  const record = saved[opId] || {};

  function persist(area, patch) {
    const nextRecord = { ...record, [area]: { ...(record[area] || {}), ...patch, updatedAt: new Date().toISOString() } };
    const next = { ...saved, [opId]: nextRecord };
    setSaved(next);
    saveObject(TRAINING_KEY, next);
  }

  function statusFor(area) {
    const item = record[area] || {};
    if (!item.date) return { label: "No Record", cls: "neutral" };
    const ageDays = Math.floor((Date.now() - new Date(item.date).getTime()) / 86400000);
    if (ageDays > 365) return { label: "Expired", cls: "danger" };
    if (ageDays > 300) return { label: "Expiring Soon", cls: "warn" };
    return { label: "Current", cls: "good" };
  }

  const totals = roster.map((op) => {
    const rec = saved[op.id] || {};
    const current = trainingAreas.filter((area) => {
      const item = rec[area];
      if (!item?.date) return false;
      return ((Date.now() - new Date(item.date).getTime()) / 86400000) <= 365;
    }).length;
    return { op, current };
  });

  return (
    <section>
      <div className="section-head">
        <div>
          <h2>Training Tracker</h2>
          <p className="muted">Track operator training currency across tactical skill areas.</p>
        </div>
      </div>

      <div className="dashboard-rings roster-command-rings">
        <div className="dashboard-ring-card tone-blue"><span className="ring-glow" /><div className="ring-value">{roster.length}</div><div className="ring-label">Operators</div><div className="ring-sub">tracked</div></div>
        <div className="dashboard-ring-card tone-green"><span className="ring-glow" /><div className="ring-value">{totals.filter((x) => x.current >= 5).length}</div><div className="ring-label">Ready</div><div className="ring-sub">5+ current areas</div></div>
        <div className="dashboard-ring-card tone-gold"><span className="ring-glow" /><div className="ring-value">{totals.filter((x) => x.current > 0 && x.current < 5).length}</div><div className="ring-label">Partial</div><div className="ring-sub">some current areas</div></div>
        <div className="dashboard-ring-card tone-red"><span className="ring-glow" /><div className="ring-value">{totals.filter((x) => x.current === 0).length}</div><div className="ring-label">Deficient</div><div className="ring-sub">no current records</div></div>
      </div>

      <div className="panel ops-tool-panel">
        <label>
          <span>Operator</span>
          <select value={selectedOperator?.id || ""} onChange={(e) => setSelectedOperatorId(e.target.value)}>
            {roster.map((op) => <option key={op.id} value={op.id}>{op.name} — {op.role}</option>)}
          </select>
        </label>
      </div>

      <div className="training-grid">
        {trainingAreas.map((area) => {
          const item = record[area] || {};
          const stat = statusFor(area);
          return (
            <div className="training-card" key={area}>
              <div className="training-card-head"><b>{area}</b><span className={`badge ${stat.cls}`}>{stat.label}</span></div>
              <label><span>Last Training Date</span><input type="date" value={item.date || ""} onChange={(e) => persist(area, { date: e.target.value })} /></label>
              <label><span>Notes</span><textarea rows="3" value={item.notes || ""} onChange={(e) => persist(area, { notes: e.target.value })} /></label>
            </div>
          );
        })}
      </div>
    </section>
  );
}


function EquipmentReadiness({ equipment, setEquipment, roster = [] }) {
  const [draft, setDraft] = useState({
    name: "",
    category: "Protective",
    assigned: "",
    serial: "",
    status: "Ready",
    total: 1,
    ready: 1,
    notes: ""
  });

  function update(id, field, value) {
    setEquipment(
      equipment.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  }

  function remove(id) {
    if (!window.confirm("Delete this equipment item?")) return;
    setEquipment(equipment.filter((item) => item.id !== id));
  }

  function addEquipment(e) {
    e.preventDefault();

    if (!draft.name.trim()) {
      alert("Equipment name required.");
      return;
    }

    const item = {
      id: `eq_${Date.now()}`,
      ...draft
    };

    setEquipment([item, ...equipment]);

    setDraft({
      name: "",
      category: "Protective",
      assigned: "",
      serial: "",
      status: "Ready",
      total: 1,
      ready: 1,
      notes: ""
    });
  }

  return (
    <section>
      <div className="section-head">
        <div>
          <h2>Equipment Management</h2>
          <p className="muted">
            Tactical inventory, readiness, assignments, and equipment tracking.
          </p>
        </div>
      </div>

      <div className="panel" style={{ marginBottom: "16px" }}>
        <form onSubmit={addEquipment} className="form-grid">
          <label>
            <span>Equipment Name</span>
            <input
              value={draft.name}
              onChange={(e) =>
                setDraft({ ...draft, name: e.target.value })
              }
            />
          </label>

          <label>
            <span>Category</span>
            <select
              value={draft.category}
              onChange={(e) =>
                setDraft({ ...draft, category: e.target.value })
              }
            >
              <option>Protective</option>
              <option>Breaching</option>
              <option>Medical</option>
              <option>Comms</option>
              <option>Vehicle</option>
              <option>Weapon Support</option>
              <option>Surveillance</option>
            </select>
          </label>

          <label>
            <span>Assigned To</span>
            <select
              value={draft.assigned}
              onChange={(e) =>
                setDraft({ ...draft, assigned: e.target.value })
              }
            >
              <option value="">Unassigned</option>
              {roster.map((op) => (
                <option key={op.id}>{op.name}</option>
              ))}
            </select>
          </label>

          <label>
            <span>Serial / Asset #</span>
            <input
              value={draft.serial}
              onChange={(e) =>
                setDraft({ ...draft, serial: e.target.value })
              }
            />
          </label>

          <label>
            <span>Status</span>
            <select
              value={draft.status}
              onChange={(e) =>
                setDraft({ ...draft, status: e.target.value })
              }
            >
              <option>Ready</option>
              <option>Check</option>
              <option>Out of Service</option>
            </select>
          </label>

          <label>
            <span>Quantity</span>
            <input
              type="number"
              value={draft.total}
              onChange={(e) =>
                setDraft({ ...draft, total: e.target.value })
              }
            />
          </label>

          <label className="full">
            <span>Notes</span>
            <textarea
              rows="3"
              value={draft.notes}
              onChange={(e) =>
                setDraft({ ...draft, notes: e.target.value })
              }
            />
          </label>

          <button className="primary" type="submit">
            Add Equipment
          </button>
        </form>
      </div>

      <div className="equipment-list">
        {equipment.map((item) => {
          const pct = item.total
            ? Math.round(
                (Number(item.ready || 0) /
                  Number(item.total || 1)) *
                  100
              )
            : 100;

          return (
            <div className="equipment-row" key={item.id}>
              <div>
                <b>{item.name}</b>
                <small>
                  {item.category} • Assigned:{" "}
                  {item.assigned || "Unassigned"}
                </small>
                <small>
                  Serial: {item.serial || "N/A"}
                </small>
                <small>{item.notes}</small>
              </div>

              <div className="progress-wrap">
                <div className="progress">
                  <span style={{ width: `${pct}%` }} />
                </div>
                <strong>{pct}%</strong>
              </div>

              <div style={{ display: "grid", gap: "8px" }}>
                <select
                  value={item.status}
                  onChange={(e) =>
                    update(item.id, "status", e.target.value)
                  }
                >
                  <option>Ready</option>
                  <option>Check</option>
                  <option>Out of Service</option>
                </select>

                <button
                  className="danger-text"
                  onClick={() => remove(item.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Roster({ roster, setRoster }) {
  function update(id, field, value) {
    setRoster(roster.map((item) => item.id === id ? { ...item, [field]: value } : item));
  }
  return (
    <section>
      <div className="section-head"><div><h2>Team Roster / Certifications</h2><p className="muted">Operator roles, specialty positions, status, and certification tracking.</p></div></div>
      <div className="grid cards">
        {roster.map((op) => (
          <div className="card" key={op.id}>
            <div className="card-title">{op.name}</div>
            <span className={`badge ${op.status === "Available" ? "good" : op.status === "Training" ? "warn" : "neutral"}`}>{op.status}</span>
            <p><b>Role:</b> {op.role}</p>
            <p className="muted">{op.certs}</p>
            <select value={op.status} onChange={(e) => update(op.id, "status", e.target.value)}><option>Available</option><option>On Operation</option><option>Training</option><option>Unavailable</option></select>
          </div>
        ))}
      </div>
    </section>
  );
}

function Aars({ operations, onExport, onEditOperation, onSave }) {
  const firstAarOperation = operations.find((op) => op.aar || op.status === "Debrief" || op.status === "Closed") || operations[0] || null;
  const [selectedOperationId, setSelectedOperationId] = useState(firstAarOperation?.id || "");

  useEffect(() => {
    if (!selectedOperationId && operations[0]?.id) {
      setSelectedOperationId(operations[0].id);
    }
  }, [operations, selectedOperationId]);

  const selectedOperation = operations.find((op) => op.id === selectedOperationId) || firstAarOperation;

  function buildAarDraft(operation) {
    if (!operation) {
      return {
        createdAt: new Date().toISOString(),
        operationName: "",
        operationDate: "",
        location: "",
        commander: "",
        missionType: "",
        objective: "",
        synopsis: "",
        outcome: "",
        personnelSummary: "",
        whatWentWell: "",
        deficiencies: "",
        safetyConcerns: "",
        equipmentIssues: "",
        communicationIssues: "",
        trainingRecommendations: "",
        correctiveActions: "",
        followUpAssignments: ""
      };
    }

    return {
      createdAt: operation.aar?.createdAt || new Date().toISOString(),
      operationName: operation.aar?.operationName || operation.title || "",
      operationDate: operation.aar?.operationDate || operation.operationDate || "",
      location: operation.aar?.location || operation.location || "",
      commander: operation.aar?.commander || operation.commander || "",
      missionType: operation.aar?.missionType || operation.operationType || operation.missionType || "",
      objective: operation.aar?.objective || operation.objective || "",
      synopsis: operation.aar?.synopsis || "",
      outcome: operation.aar?.outcome || "",
      personnelSummary: operation.aar?.personnelSummary || operation.assignedOperators || "",
      whatWentWell: operation.aar?.whatWentWell || "",
      deficiencies: operation.aar?.deficiencies || "",
      safetyConcerns: operation.aar?.safetyConcerns || "",
      equipmentIssues: operation.aar?.equipmentIssues || "",
      communicationIssues: operation.aar?.communicationIssues || "",
      trainingRecommendations: operation.aar?.trainingRecommendations || "",
      correctiveActions: operation.aar?.correctiveActions || "",
      followUpAssignments: operation.aar?.followUpAssignments || ""
    };
  }

  const [draft, setDraft] = useState(() => buildAarDraft(selectedOperation));

  useEffect(() => {
    setDraft(buildAarDraft(selectedOperation));
  }, [selectedOperationId]);

  function updateDraft(field, value) {
    setDraft((prev) => ({ ...prev, [field]: value }));
  }

  function saveAar() {
    if (!selectedOperation) return;

    const now = new Date().toISOString();
    const updated = {
      ...selectedOperation,
      status: selectedOperation.status === "Closed" ? "Closed" : "Debrief",
      aar: {
        ...(selectedOperation.aar || {}),
        ...draft,
        updatedAt: now
      },
      updatedAt: now,
      timeline: [
        ...(Array.isArray(selectedOperation.timeline) ? selectedOperation.timeline : []),
        { id: makeId("tl"), time: now, title: "AAR updated", detail: "After-action review details saved." }
      ]
    };

    onSave?.(updated);
    alert("AAR saved.");
  }

  function exportCurrentAar() {
    if (!selectedOperation) return;
    onExport({ ...selectedOperation, aar: { ...(selectedOperation.aar || {}), ...draft } });
  }

  const aarOperations = operations.filter((op) => op.aar || op.status === "Debrief" || op.status === "Closed");

  return (
    <section>
      <div className="section-head">
        <div>
          <h2>After-Action Reviews</h2>
          <p className="muted">Create, edit, save, and export tactical after-action reviews linked to operation timelines.</p>
        </div>
        <div className="mission-actions">
          <button onClick={() => selectedOperation && onEditOperation(selectedOperation)}>Open Operation</button>
          <button onClick={saveAar} className="primary" disabled={!selectedOperation}>Save AAR</button>
          <button onClick={exportCurrentAar} disabled={!selectedOperation}>Export PDF</button>
        </div>
      </div>

      {operations.length === 0 ? (
        <div className="empty">No operations exist yet. Create an operation first, then return here to build the AAR.</div>
      ) : (
        <div className="aar-layout">
          <div className="panel aar-list-panel">
            <h2>Operations</h2>
            <p className="muted">Select any operation to start or continue an AAR.</p>
            <div className="mission-list compact">
              {operations.map((operation) => (
                <button
                  key={operation.id}
                  className={`aar-operation-select ${selectedOperationId === operation.id ? "active" : ""}`}
                  onClick={() => setSelectedOperationId(operation.id)}
                >
                  <b>{operation.title}</b>
                  <small>{operation.operationType || operation.missionType || "Operation"} • {operation.status}</small>
                  <span className={`badge ${operation.aar ? "good" : operation.status === "Closed" || operation.status === "Debrief" ? "warn" : "neutral"}`}>
                    {operation.aar ? "AAR Started" : operation.status === "Closed" || operation.status === "Debrief" ? "Needs Review" : "Not Started"}
                  </span>
                </button>
              ))}
            </div>

            {aarOperations.length === 0 ? (
              <div className="empty" style={{ marginTop: "12px" }}>No AARs have been saved yet. Select an operation and hit Save AAR to start one.</div>
            ) : null}
          </div>

          <div className="panel aar-editor-panel">
            {selectedOperation ? (
              <>
                <div className="aar-editor-head">
                  <div>
                    <h2>{draft.operationName || selectedOperation.title}</h2>
                    <p className="muted">{draft.missionType || "Operation"} • {draft.location || "No location"}</p>
                  </div>
                  <span className="badge good">Editable AAR</span>
                </div>

                <div className="form-grid">
                  <label>
                    <span>Operation Name</span>
                    <input value={draft.operationName} onChange={(e) => updateDraft("operationName", e.target.value)} />
                  </label>
                  <label>
                    <span>Date</span>
                    <input value={draft.operationDate} onChange={(e) => updateDraft("operationDate", e.target.value)} />
                  </label>
                  <label>
                    <span>Location</span>
                    <input value={draft.location} onChange={(e) => updateDraft("location", e.target.value)} />
                  </label>
                  <label>
                    <span>Commander</span>
                    <input value={draft.commander} onChange={(e) => updateDraft("commander", e.target.value)} />
                  </label>
                  <label>
                    <span>Operation Type</span>
                    <input value={draft.missionType} onChange={(e) => updateDraft("missionType", e.target.value)} />
                  </label>
                  <label>
                    <span>Outcome</span>
                    <input value={draft.outcome} onChange={(e) => updateDraft("outcome", e.target.value)} placeholder="Completed / Arrest made / Evidence recovered / No contact" />
                  </label>
                </div>

                <div className="aar-editor-grid">
                  <label className="full">
                    <span>Objective</span>
                    <textarea rows="3" value={draft.objective} onChange={(e) => updateDraft("objective", e.target.value)} />
                  </label>
                  <label className="full">
                    <span>Synopsis</span>
                    <textarea rows="5" value={draft.synopsis} onChange={(e) => updateDraft("synopsis", e.target.value)} placeholder="Brief narrative of what occurred." />
                  </label>
                  <label className="full">
                    <span>Personnel / Assignments Summary</span>
                    <textarea rows="4" value={draft.personnelSummary} onChange={(e) => updateDraft("personnelSummary", e.target.value)} />
                  </label>
                  <label>
                    <span>What Went Well</span>
                    <textarea rows="5" value={draft.whatWentWell} onChange={(e) => updateDraft("whatWentWell", e.target.value)} />
                  </label>
                  <label>
                    <span>Deficiencies / Issues</span>
                    <textarea rows="5" value={draft.deficiencies} onChange={(e) => updateDraft("deficiencies", e.target.value)} />
                  </label>
                  <label>
                    <span>Safety Concerns</span>
                    <textarea rows="5" value={draft.safetyConcerns} onChange={(e) => updateDraft("safetyConcerns", e.target.value)} />
                  </label>
                  <label>
                    <span>Equipment Issues</span>
                    <textarea rows="5" value={draft.equipmentIssues} onChange={(e) => updateDraft("equipmentIssues", e.target.value)} />
                  </label>
                  <label>
                    <span>Communication Issues</span>
                    <textarea rows="5" value={draft.communicationIssues} onChange={(e) => updateDraft("communicationIssues", e.target.value)} />
                  </label>
                  <label>
                    <span>Training Recommendations</span>
                    <textarea rows="5" value={draft.trainingRecommendations} onChange={(e) => updateDraft("trainingRecommendations", e.target.value)} />
                  </label>
                  <label>
                    <span>Corrective Actions</span>
                    <textarea rows="5" value={draft.correctiveActions} onChange={(e) => updateDraft("correctiveActions", e.target.value)} />
                  </label>
                  <label>
                    <span>Follow-Up Assignments</span>
                    <textarea rows="5" value={draft.followUpAssignments} onChange={(e) => updateDraft("followUpAssignments", e.target.value)} />
                  </label>
                </div>

                <div className="panel" style={{ marginTop: "14px" }}>
                  <h2>Linked Timeline</h2>
                  <div className="timeline-feed">
                    {(selectedOperation.timeline || []).length ? selectedOperation.timeline.map((entry) => (
                      <div className="timeline-entry" key={entry.id}>
                        <div className="dot" />
                        <div>
                          <b>{entry.title}</b>
                          <small>{new Date(entry.time).toLocaleString()}</small>
                          <p>{entry.detail}</p>
                        </div>
                      </div>
                    )) : <div className="empty">No timeline entries linked yet.</div>}
                  </div>
                </div>
              </>
            ) : (
              <div className="empty">Select an operation to build an AAR.</div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function Timeline({ operations, onUpdateOperation }) {
  const [selectedOperationId, setSelectedOperationId] = useState(operations[0]?.id || "");

  useEffect(() => {
    if (!selectedOperationId && operations[0]?.id) {
      setSelectedOperationId(operations[0].id);
    }
  }, [operations, selectedOperationId]);

  const selectedOperation = operations.find((op) => op.id === selectedOperationId) || operations[0] || null;

  const entries = operations
    .flatMap((op) =>
      (op.timeline || []).map((t) => ({
        ...t,
        operationId: op.id,
        operation: op.title,
        status: op.status
      }))
    )
    .sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0));

  function deleteTimelineEvent(operationId, eventId) {
    const target = operations.find((op) => op.id === operationId);
    if (!target) return;

    const ok = window.confirm("Delete this timeline entry?");
    if (!ok) return;

    const updatedOperation = {
      ...target,
      updatedAt: new Date().toISOString(),
      timeline: (target.timeline || []).filter((event) => event.id !== eventId)
    };

    onUpdateOperation(updatedOperation);
  }

  function wipeSelectedTimeline() {
    if (!selectedOperation) return;

    const ok = window.confirm(
      `Wipe the entire timeline for "${selectedOperation.title}"? This cannot be undone.`
    );
    if (!ok) return;

    const updatedOperation = {
      ...selectedOperation,
      updatedAt: new Date().toISOString(),
      timeline: []
    };

    onUpdateOperation(updatedOperation);
  }

  return (
    <section>
      <div className="section-head timeline-management-head">
        <div>
          <h2>Operations Timeline</h2>
          <p className="muted">Auto-generated operation activity and status history.</p>
        </div>

        <div className="timeline-management-actions">
          <select
            value={selectedOperation?.id || ""}
            onChange={(e) => setSelectedOperationId(e.target.value)}
            aria-label="Select operation timeline to wipe"
          >
            {operations.map((op) => (
              <option key={op.id} value={op.id}>
                {op.title}
              </option>
            ))}
          </select>

          <button
            className="danger-text"
            onClick={wipeSelectedTimeline}
            disabled={!selectedOperation}
          >
            Wipe Selected Timeline
          </button>
        </div>
      </div>

      <div className="timeline-feed">
        {entries.length === 0 ? (
          <div className="empty">No timeline entries yet.</div>
        ) : (
          entries.map((entry) => (
            <div className="timeline-entry" key={`${entry.operationId}_${entry.id}`}>
              <div className="dot" />
              <div>
                <div className="timeline-entry-head">
                  <div>
                    <b>{entry.title}</b>
                    <small>{entry.operation} • {new Date(entry.time).toLocaleString()}</small>
                  </div>

                  <button
                    className="danger-text timeline-delete-btn"
                    onClick={() => deleteTimelineEvent(entry.operationId, entry.id)}
                  >
                    Delete
                  </button>
                </div>
                <p>{entry.detail}</p>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
